import { api } from './client';
import type { User } from '@stores/authStore';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: User;
}

interface RegisterResponse extends LoginResponse {}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export const authApi = {
  /**
   * Login with email and password
   */

  login: async (email: string, password: string): Promise<LoginResponse> => {
    return api.post<LoginResponse>('/auth/login', {
      login: email,  // Backend aspetta "login" field
      password,
  });
},



  /**
   * Register new user
   */
  register: async (
    email: string,
    password: string,
    username: string
  ): Promise<RegisterResponse> => {
    return api.post<RegisterResponse>('/auth/register', {
      email,
      password,
      username,
    });
  },

  /**
   * Refresh access token
   */
  refresh: async (refreshToken: string): Promise<RefreshResponse> => {
    return api.post<RefreshResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });
  },

  /**
   * Logout (invalidate tokens)
   */
  logout: async (): Promise<void> => {
    return api.post('/auth/logout');
  },

  /**
   * Get current user info
   */
  me: async (): Promise<User> => {
    return api.get<User>('/auth/me');
  },

  /**
   * Update user profile
   */
  updateProfile: async (updates: Partial<User>): Promise<User> => {
    return api.patch<User>('/auth/me', updates);
  },

  /**
   * Change password
   */
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<void> => {
    return api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  /**
   * Request password reset
   */
  requestPasswordReset: async (email: string): Promise<void> => {
    return api.post('/auth/forgot-password', { email });
  },

  /**
   * Reset password with token
   */
  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    return api.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
  },

  /**
   * OAuth login (Lichess, Chess.com)
   */
  oauthLogin: async (
    provider: 'lichess' | 'chesscom',
    code: string
  ): Promise<LoginResponse> => {
    return api.post<LoginResponse>(`/auth/oauth/${provider}`, { code });
  },

  /**
   * Link external account
   */
  linkAccount: async (
    provider: 'lichess' | 'chesscom',
    code: string
  ): Promise<void> => {
    return api.post(`/auth/link/${provider}`, { code });
  },

  /**
   * Unlink external account
   */
  unlinkAccount: async (provider: 'lichess' | 'chesscom'): Promise<void> => {
    return api.delete(`/auth/link/${provider}`);
  },
};
